import { useState, useEffect, useCallback, useRef } from "react";


// Define request options type to avoid ESLint issues with RequestInit
interface FetchOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
}

// Define types for API responses
interface CompositionResponse {
  content?: string;
}

interface TipTapNode {
  content?: Array<{ text?: string }>;
}

interface TipTapDocument {
  content?: TipTapNode[];
}

// Composing event structure
export interface ComposingEvent {
  participantId: string;
  threadId: string;
  conversationId: string;
  workspaceId: string;
  content: string;
  timestamp: string;
}

export interface CompositionApiConfig {
  // API endpoints
  getEndpoint: string;
  saveEndpoint: string;
  deleteEndpoint: string;
  submitEndpoint: string;

  // Request configuration
  buildGetUrl?: (participantId: string) => string;
  buildSaveRequest?: (data: CompositionSaveData) => {
    url: string;
    options: FetchOptions;
  };
  buildDeleteUrl?: (participantId: string) => string;
  buildSubmitUrl?: (participantId: string) => string;

  // Response parsing
  parseGetResponse?: (response: unknown) => CompositionResponse | null;
  parseSaveResponse?: (response: unknown) => unknown;

  // Event emission
  onComposing?: (event: ComposingEvent) => void | Promise<void>;
}

export interface CompositionSaveData {
  participantId: string;
  content: string;
  threadId: string;
  conversationId: string;
  workspaceId: string;
}

interface UseCompositionProps {
  participantId: string;
  threadId: string;
  conversationId: string;
  workspaceId: string;
  apiConfig: CompositionApiConfig;
  onSubmitMessage: (content: string) => void | Promise<void>;
  draftText: string;
  setDraftText: (text: string) => void;
  debounceMs?: number;
}

/**
 * Configurable composition hook that works with different API backends
 * Supports both local Bun server and Cloudflare Workers
 */
export function useComposition({
  participantId,
  threadId,
  conversationId,
  workspaceId,
  apiConfig,
  onSubmitMessage,
  draftText,
  setDraftText,
  debounceMs = 1000,
}: UseCompositionProps) {
  const [hasText, setHasText] = useState(false);
  const [initialContent, setInitialContent] = useState("");
  const [_getTextFn, setGetTextFn] = useState<(() => string) | null>(null);
  const [getContentWithTagsFn, setGetContentWithTagsFn] = useState<
    (() => string) | null
  >(null);
  const [clearTextFn, setClearTextFn] = useState<(() => void) | null>(null);
  const [setTextFn, setSetTextFn] = useState<((text: string) => void) | null>(
    null,
  );

  // Use refs to store the latest functions to avoid infinite loops
  const getContentWithTagsRef = useRef<(() => string) | null>(null);
  getContentWithTagsRef.current = getContentWithTagsFn;

  // Auto-save composition with debouncing - triggered by content changes
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMountRef = useRef(true);

  // Build API URLs using provided config or defaults
  const buildGetUrl = useCallback(
    (pid: string) => {
      if (apiConfig.buildGetUrl) {
        return apiConfig.buildGetUrl(pid);
      }
      return `${apiConfig.getEndpoint}?participantId=${pid}`;
    },
    [apiConfig],
  );

  const buildSaveRequest = useCallback(
    (data: CompositionSaveData) => {
      if (apiConfig.buildSaveRequest) {
        return apiConfig.buildSaveRequest(data);
      }
      return {
        url: apiConfig.saveEndpoint,
        options: {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        },
      };
    },
    [apiConfig],
  );

  const buildDeleteUrl = useCallback(
    (pid: string) => {
      if (apiConfig.buildDeleteUrl) {
        return apiConfig.buildDeleteUrl(pid);
      }
      return `${apiConfig.deleteEndpoint}?participantId=${pid}`;
    },
    [apiConfig],
  );

  const buildSubmitUrl = useCallback(
    (pid: string) => {
      if (apiConfig.buildSubmitUrl) {
        return apiConfig.buildSubmitUrl(pid);
      }
      return `${apiConfig.submitEndpoint}?participantId=${pid}`;
    },
    [apiConfig],
  );

  // Load existing composition on startup (only once)
  useEffect(() => {
    if (!participantId) return;

    const loadExistingComposition = async () => {
      // Extract actual text content from TipTap JSON to check if editor has meaningful content
      let hasExistingContent = false;
      if (draftText && draftText.trim()) {
        try {
          const parsed = JSON.parse(draftText) as TipTapDocument;
          const textContent =
            parsed.content
              ?.map(
                (node) =>
                  node.content
                    ?.map((textNode) => textNode.text || "")
                    .join("") || "",
              )
              .join("") || "";
          hasExistingContent = textContent.trim().length > 0;
        } catch {
          // If not JSON, treat as plain text
          hasExistingContent = draftText.trim().length > 0;
        }
      }

      if (hasExistingContent) {
        console.log("Editor already has content, skipping load");
        return;
      }

      try {
        const url = buildGetUrl(participantId);
        const response = await fetch(url);
        if (response.ok) {
          const rawData = await response.json();
          const composition = apiConfig.parseGetResponse
            ? apiConfig.parseGetResponse(rawData)
            : (rawData as CompositionResponse);

          if (composition?.content) {
            setInitialContent(composition.content);
            console.log(
              "📄 Loaded existing composition:",
              composition.content.substring(0, 50),
            );

            // Set the content in the editor if setText function is available
            if (setDraftText) {
              setDraftText(composition.content);
              console.log("✅ Set content in editor:", composition.content.substring(0, 50));
            } else {
              console.log(
                "⏳ Editor not ready, content will be set when editor initializes",
              );
            }
          }
        }
      } catch (error) {
        console.error("Failed to load existing composition:", error);
      }
    };

    loadExistingComposition();
  }, [participantId, buildGetUrl, apiConfig, draftText, setDraftText]);

  // Auto-save composition as user types
  const saveComposition = useCallback(
    async (content: string) => {
      if (!content.trim()) return;

      console.log("💾 Saving composition:", content.substring(0, 50));

      try {
        const saveData: CompositionSaveData = {
          participantId,
          content,
          threadId,
          conversationId,
          workspaceId,
        };

        const { url, options } = buildSaveRequest(saveData);
        const response = await fetch(url, options);

        if (response.ok && apiConfig.parseSaveResponse) {
          const result = await response.json();
          apiConfig.parseSaveResponse(result);
        }

        // // Emit composing event after successful save
        // if (apiConfig.onComposing) {
        //   const composingEvent: ComposingEvent = {
        //     participantId,
        //     threadId,
        //     conversationId,
        //     workspaceId,
        //     content,
        //     timestamp: new Date().toISOString(),
        //   };
          
        //   try {
        //     await apiConfig.onComposing(composingEvent);
        //     console.log("📡 Composing event emitted");
        //   } catch (error) {
        //     console.error("Failed to emit composing event:", error);
        //   }
        // }
      } catch (error) {
        console.error("Failed to save composition:", error);
      }
    },
    [
      participantId,
      threadId,
      conversationId,
      workspaceId,
      buildSaveRequest,
      apiConfig,
    ],
  );

  const emitComposingEvent = useCallback(async (content: string) => {
    // Emit composing event after successful save
    if (apiConfig.onComposing) {
      const composingEvent: ComposingEvent = {
        participantId,
        threadId,
        conversationId,
        workspaceId,
        content,
        timestamp: new Date().toISOString(),
      };
      
      try {
        await apiConfig.onComposing(composingEvent);
        console.log("📡 Composing event emitted");
      } catch (error) {
        console.error("Failed to emit composing event:", error);
      }
    }
  }, [participantId, threadId, conversationId, workspaceId, apiConfig]);

  // Watch draft text changes from TipTap's built-in debounced system
  useEffect(() => {
    // Skip saving on initial mount to prevent circular saves
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      console.log("Skipping save on initial mount");
      return;
    }

    if (draftText && draftText.trim()) {
      // console.log(
      //   "Draft text changed, adding second layer debounce:",
      //   draftText.substring(0, 50),
      // );

      // Clear previous timeout (second layer of debouncing)
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Add configurable debounce on top of TipTap's 500ms debounce
      saveTimeoutRef.current = setTimeout(() => {
        // Extract plain text from JSON if needed
        let contentToSave = draftText;
        try {
          const parsed = JSON.parse(draftText) as TipTapDocument;
          // If it's JSON, extract text content (this is how TipTap saves rich content)
          contentToSave =
            parsed.content
              ?.map(
                (node) =>
                  node.content
                    ?.map((textNode) => textNode.text || "")
                    .join("") || "",
              )
              .join("") || draftText;
        } catch {
          console.log("Draft text is not JSON, using as-is");
          // If not JSON, use as-is
        }

        if (contentToSave.trim()) {
          saveComposition(contentToSave);
        }
        saveTimeoutRef.current = null;
      }, debounceMs);
    }
  }, [draftText, saveComposition, debounceMs]);

  const onTextChange = useCallback((newHasText: boolean) => {
    setHasText(newHasText);
  }, []);

  // SAVE FIRST, THEN SEND
  const handleSubmit = useCallback(async () => {
    if (!getContentWithTagsFn) {
      console.error("getContentWithTagsFn is not set");
      return;
    }

    const content = getContentWithTagsFn();
    console.log("\nSubmitting message:", content.substring(0, 50));

    await saveComposition(content);

    const submitUrl = buildSubmitUrl(participantId);
    console.log("Submit URL:", submitUrl);
    const response = await fetch(submitUrl, { 
      method: "POST", 
      body: JSON.stringify({ timestamp: new Date().toISOString() }),
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // ✅ Include cookies for authentication
    });
    if (response.ok) {
      console.log("\nMessage submitted successfully");
      const result = await response.json();
      console.log("\nMessage submitted successfully:", result);
    } else {
      console.error("Failed to submit message");
    }

    try {
      await onSubmitMessage(content);
      console.log("\nMessage callback called successfully");
    } catch (error) {
      console.error("Failed to submit message:", error);
    }


    // try {
    //   const deleteUrl = buildDeleteUrl(participantId);
    //   await fetch(deleteUrl, { method: "DELETE" });
    // } catch (error) {
    //   console.error("Failed to delete composition:", error);
    // }
    // console.log("\nComposition deleted successfully");

    // Clear the composition after "sending"
    // Cancel pending auto-save to prevent race condition
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    // Clear editor and draft atom
    if (clearTextFn) clearTextFn();

    // Also clear draft text atom to prevent sync effect from reloading old content
    setDraftText("");
  }, [
    participantId,
    getContentWithTagsFn,
    clearTextFn,
    buildDeleteUrl,
    onSubmitMessage,
    saveComposition,
    setDraftText,
  ]);

  const onEditorReady = useCallback(
    (
      getText: () => string,
      getContentWithTags: () => string,
      clearText: () => void,
      initialHasText: boolean,
      setText: (text: string) => void,
    ) => {
      setGetTextFn(() => getText);
      setGetContentWithTagsFn(() => getContentWithTags);
      setClearTextFn(() => clearText);
      setSetTextFn(() => setText);
      setHasText(initialHasText);

      // // If we have initial content and editor is ready, set it now
      // if (initialContent && !initialHasText) {
      //   console.log(
      //     "🔄 Setting initial content in editor:",
      //     initialContent.substring(0, 50),
      //   );
      //   setText(initialContent);
      // }
    },
    [initialContent],
  );

  return {
    hasText,
    initialContent,
    onTextChange,
    handleSubmit,
    onEditorReady,
    clearTextFn,
    setTextFn,
    setGetTextFn,
    setGetContentWithTagsFn,
    getContentWithTagsFn: getContentWithTagsFn || (() => draftText.trim()),
    setClearTextFn,
    setSetTextFn,
    setHasText,
    emitComposingEvent,
  };
}

// Preset configurations for common use cases

export const createLocalComposerConfig = (
  baseUrl: string,
  options?: {
    onComposing?: (event: ComposingEvent) => void | Promise<void>;
  },
): CompositionApiConfig => ({
  getEndpoint: `${baseUrl}/api/composition`,
  saveEndpoint: `${baseUrl}/api/composition`,
  deleteEndpoint: `${baseUrl}/api/composition`,
  submitEndpoint: `${baseUrl}/api/sendMessage`,
  onComposing: options?.onComposing,
});

export const createCloudflareComposerConfig = (
  baseUrl: string,
  options?: {
    onComposing?: (event: ComposingEvent) => void | Promise<void>;
  },
): CompositionApiConfig => ({
  getEndpoint: `${baseUrl}/api/composition`,
  saveEndpoint: `${baseUrl}/api/composition`,
  deleteEndpoint: `${baseUrl}/api/composition`,
  submitEndpoint: `${baseUrl}/api/sendMessage`,

  buildSaveRequest: (data: CompositionSaveData) => ({
    url: `${baseUrl}/api/composition?participantId=${data.participantId}`,
    options: {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    },
  }),

  buildDeleteUrl: (participantId: string) =>
    `${baseUrl}/api/composition?participantId=${participantId}`,
  
  onComposing: options?.onComposing,
});

// WebSocket helper for composing events
export const createWebSocketComposingHandler = (
  websocket: WebSocket | (() => WebSocket | null),
) => {
  return async (event: ComposingEvent) => {
    const ws = typeof websocket === 'function' ? websocket() : websocket;
    
    if (ws && ws.readyState === WebSocket.OPEN) {
      const message = JSON.stringify({
        type: 'composing',
        data: event,
      });
      ws.send(message);
      console.log('📡 Sent composing event via WebSocket');
    } else {
      console.warn('⚠️ WebSocket not available for composing event');
    }
  };
};
